import { useEffect, useState} from "react";
import { Modal, Button, Text, Group, Title, TextInput, Stack, Textarea, Select, Divider, Box, Grid, Card, SimpleGrid } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { notifyError } from "../Toast";
import { useDispatch } from "react-redux";
import { saveProcess } from "../../actions/processActions";

const initialData = {
  nombre: '',
  descripcion: '',
  matrizId: '',
  etapas: [],
}

const mapStages = (stages = []) =>
  stages.map((et) => ({
    ...et,
    fechaInicio: et.fechaInicio?.slice(0, 10) || '',
    fechaCierre: et.fechaCierre?.slice(0, 10) || '',
    subEtapas: (et.subEtapas || []).map(sub => ({
      ...sub,
      fechaInicio: sub.fechaInicio?.slice(0, 10) || '',
      fechaCierre: sub.fechaCierre?.slice(0, 10) || '',
    }))
  })
);

const ModalProcessEditor = ({ opened, onClose, process, stages = [], matrix = [] }) => {

  const isNew = !process;
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width: 50em)');
  const {user} = useSelector((state) => state.auth);
  const {saving, error} = useSelector((state) => state.process)
  const [formData, setFormData] = useState(initialData);
  
  useEffect(() => {
    if (!opened) return;

    if (process) {
      setFormData({
        nombre: process.nombre || '',
        descripcion: process.descripcion || '',
        matrizId: process.matriz?.Id || '',
        etapas: mapStages(process.etapas),
      });
    } else {
      setFormData({
        nombre: '',
        descripcion: '',
        matrizId: '',
        etapas: mapStages(stages),
      });
    }
  }, [opened, process]);

  useEffect(() => {
    if (!saving && !error) {
      handleClose();
    }
  }, [saving, error]);
  
  const updateEtapaFecha = (index, campo, valor) => {
    const nuevasEtapas = [...formData.etapas];
    nuevasEtapas[index][campo] = valor;
    setFormData({ ...formData, etapas: nuevasEtapas });
  };

  const updateSubEtapaFecha = (etapaId, subId, campo, valor) => {
    const nuevasEtapas = formData.etapas.map((et) => {
      if (et.Id !== etapaId) return et;
      const nuevasSub = et.subEtapas.map((sub) => {
        if (sub.Id !== subId) return sub;
        return { ...sub, [campo]: valor };
      });
      return { ...et, subEtapas: nuevasSub };
    });
    setFormData({ ...formData, etapas: nuevasEtapas });
  };

  const handleClose = () => {
    if (!saving){
      setFormData(initialData);
      onClose();
    }
  };

  const handleSave = () => {
    if (!formData.nombre.trim() || !formData.descripcion.trim() || !formData.matrizId) {
      notifyError("Por favor completá todos los campos obligatorios.");
      return;
    }

    const fechasInvalidas = formData.etapas.some(et =>
      !et.fechaInicio || !et.fechaCierre ||
      (et.subEtapas?.some(sub => !sub.fechaInicio || !sub.fechaCierre))
    );
    if (fechasInvalidas) {
      notifyError("Completá todas las fechas de las etapas y subetapas.");
      return;
    }

    const payload = {
      Id: process?.Id || '00000000-0000-0000-0000-000000000000',
      nombre: formData.nombre.trim(),
      descripcion: formData.descripcion.trim(),
      matriz: { Id: formData?.matrizId  || '00000000-0000-0000-0000-000000000000'},
      etapas: formData.etapas.map((et) => ({
        Id: et.Id,
        fechaInicio: dayjs(et.fechaInicio).toISOString(),
        fechaCierre: dayjs(et.fechaCierre).toISOString(),
        subEtapas: et.subEtapas?.map((sub) => ({
          Id: sub.Id,
          fechaInicio: dayjs(sub.fechaInicio).toISOString(),
          fechaCierre: dayjs(sub.fechaCierre).toISOString(),
        })) || [],
      })),
    };

    dispatch(saveProcess(payload,user));
  };
  
  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={(<Text size="xl" fw={600}>{isNew ? 'Crear nuevo' : 'Editar'} Proceso Evaluativo</Text>)}
      closeOnClickOutside={false}
      fullScreen={isMobile}
      size={'xl'}
      overlayProps={{
        backgroundOpacity: 0.55,
        blur: 3,
      }}
    >
      <Divider label="Detalles del Proceso" labelPosition="center" my={'sm'} />

      <Stack spacing="md">
        <TextInput
          label="Nombre del Proceso"
          placeholder="Ej: Proceso Evaluativo 2025 - Primer Semestre"
          required
          value={formData.nombre}
          onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
        />

        <Textarea
          label="Descripción"
          placeholder="Ingresar breve descripción del proceso evaluativo"
          required
          autosize
          minRows={2}
          maxRows={4}
          value={formData.descripcion}
          onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
        />

        <Select
          label="Matriz Asociada"
          placeholder="Seleccionar matriz"
          required
          value={formData.matrizId}
          onChange={(val) => setFormData({ ...formData, matrizId: val })}
          data={matrix.map((m) => ({
            value: m.Id,
            label: `${m.nombre} (${m.tematica.descripcion})`,
          }))}
        />

        <Divider label="Etapas del Proceso" labelPosition="center" mt="lg" />

        <Stack gap="md">
          {formData.etapas.map((stage, index) => (
            <Card key={stage.Id} withBorder radius="md" style={{borderColor:'#c70050'}}>
              <Text size="md" fw={500}>Etapa {stage.orden}: {stage.nombre}</Text>
              <Text size="sm">{stage.descripcion}</Text>
              <SimpleGrid cols={2} mt={'sm'}>
                <TextInput
                  type="date"
                  label="Fecha de Inicio"
                  value={stage.fechaInicio}
                  onChange={(e) => updateEtapaFecha(index, 'fechaInicio', e.target.value)}
                />
                <TextInput
                  type="date"
                  label="Fecha de Cierre"
                  value={stage.fechaCierre}
                  onChange={(e) => updateEtapaFecha(index, 'fechaCierre', e.target.value)}
                />
              </SimpleGrid>
              
              {stage.subEtapas?.length > 0 && (
                <Box mt={'lg'}>
                  <Text fw={450} size="md">Subetapas:</Text>
                  <Stack spacing="sm" pl={'xl'} mt={'xs'}>
                    {stage.subEtapas.map((sub, subIndex) => (
                      <Card key={sub.Id} p="xs" withBorder bg={'#f9f9f9'}>
                        <Text fw={450} size="sm">{stage.orden}.{sub.orden} {sub.nombre}</Text>
                        <Text size="sm" c="dimmed">{sub.descripcion}</Text>
                        <SimpleGrid cols={2}>
                          <TextInput
                            type="date"
                            label="Inicio"
                            value={sub.fechaInicio}
                            onChange={(e) =>
                              updateSubEtapaFecha(stage.Id, sub.Id, 'fechaInicio', e.target.value)
                            }
                          />
                          <TextInput
                            type="date"
                            label="Cierre"
                            value={sub.fechaCierre}
                            onChange={(e) =>
                              updateSubEtapaFecha(stage.Id, sub.Id, 'fechaCierre', e.target.value)
                            }
                          />
                        </SimpleGrid>
                      </Card>
                    ))}
                  </Stack>
                </Box>
              )}
            </Card>
          ))}
        </Stack>

        <Group mt="lg" justify="flex-end">
          <Button variant="default" onClick={handleClose} disabled={saving}>Cancelar</Button>
          <Button color="#e72166" onClick={handleSave} loading={saving}>
            {isNew ? 'Crear Proceso' : 'Guardar Cambios'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ModalProcessEditor;